import { Dispatch } from 'redux';
import axios from 'axios';
import { RootState } from '../store'; // Assuming you have a RootState type for your Redux store
import { CartItem } from '@/data/types';

// Action Types
const CREATE_PAYMENT_INTENT_REQUEST = 'CREATE_PAYMENT_INTENT_REQUEST';
const CREATE_PAYMENT_INTENT_SUCCESS = 'CREATE_PAYMENT_INTENT_SUCCESS';
const CREATE_PAYMENT_INTENT_FAILURE = 'CREATE_PAYMENT_INTENT_FAILURE';

// Action Interfaces
interface CreatePaymentIntentRequestAction {
  type: typeof CREATE_PAYMENT_INTENT_REQUEST;
}

interface CreatePaymentIntentSuccessAction {
  type: typeof CREATE_PAYMENT_INTENT_SUCCESS;
  payload: { clientSecret: string };
}

interface CreatePaymentIntentFailureAction {
  type: typeof CREATE_PAYMENT_INTENT_FAILURE;
  error: string;
}

type StripeActions =
  | CreatePaymentIntentRequestAction
  | CreatePaymentIntentSuccessAction
  | CreatePaymentIntentFailureAction;

// State Type
interface StripeState {
  clientSecret: string | null;
  loading: boolean;
  error: string | null;
}

// Initial State
const initialState: StripeState = {
  clientSecret: null,
  loading: false,
  error: null,
};

// Reducer
const stripeReducer = (state: StripeState = initialState, action: StripeActions): StripeState => {
  switch (action.type) {
    case CREATE_PAYMENT_INTENT_REQUEST:
      return { ...state, loading: true, error: null };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return { ...state, loading: false, clientSecret: action.payload.clientSecret };
    case CREATE_PAYMENT_INTENT_FAILURE:
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};

// Action Creator
export const createPaymentIntent = (
  cartItems: CartItem[],
  paymentMethodId: string,
  userEmail: string,
  customerName: string,
  coinsValueCurrency: number = 0,
  idempotencyKey?: string,
  // The net amount the UI displays and that createOrder() will record, already
  // in `selectedCurrency`. Sent so the charge, the order and the invoice are the
  // same number: the backend used to re-derive it from gross items + the raw
  // discount fields below, on a different base (it ignores the discount's city
  // restriction and treats a flat EUR value as if it were in the paid currency),
  // so the card was debited something other than what the customer validated.
  amountDueCurrency?: number,
  // Nombre de pieces Guydeez utilisees : le serveur en deduit leur valeur lui-meme
  coinsUsed: number = 0
) => async (
  dispatch: Dispatch<StripeActions>,
  getState: () => RootState
) => {
  dispatch({ type: CREATE_PAYMENT_INTENT_REQUEST });

  const userString = localStorage.getItem('user');
  let accessToken = '';

  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  if (!accessToken) {
    const errorMessage = 'User is not authenticated.' + userString;
    console.error(errorMessage);
    dispatch({ type: CREATE_PAYMENT_INTENT_FAILURE, error: errorMessage });
    return;
  }

  try {
    const state = getState();
    const discountAmount = state.orders.discountAmount || null;
    // Sent so the server can RE-READ the code (activated? in date? which
    // cities?) and recompute the amount owed itself, instead of taking the
    // client's word for the reduction.
    const discountUuid = state.orders.discountUuid || null;
    const discountType = state.orders.discountType || null;
    const discountValid = state.orders.discountValid || false;
    const selectedCurrency = state.currencies.selectedCurrency;

    const transformedCartItems = cartItems.map((item) => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
    }));

    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_SOCKET}/api/stripe/create-payment`,
      {
        items: transformedCartItems,
        discountAmount,
        discountType,
        discountValid,
        currency: selectedCurrency,
        paymentMethodId, // ✅ Pass payment method
        userEmail,
        customerName,
        coinsValueCurrency,
        coinsUsed,
        // Le devis affiche (celui du serveur) : le serveur refuse s'il ne l'est plus
        amountDueCurrency,
        discountUuid,
        idempotencyKey, // dedups an accidental repeat of THIS same charge on Stripe
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    console.log("Stripe Response:", response.data);

    if (response.data.status === "succeeded") {
      
    } else {
      dispatch({ type: CREATE_PAYMENT_INTENT_FAILURE, error: "Payment not completed." });
    }

    return response.data; // ✅ Return only relevant data
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    // `ambiguous` = we never got a clean HTTP response, so the card MAY already
    // have been charged on Stripe's side. The caller must NOT treat this as a
    // plain failure that invites a blind retry (that would re-charge).
    let ambiguous = false;
    // `price_changed` : le prix a change depuis l'affichage, RIEN n'a ete debite ;
    // l'appelant affiche le nouveau devis (`quote`) et le client paie a nouveau.
    let code: string | undefined;
    let quote: unknown;
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
      code = error.response?.data?.code;
      quote = error.response?.data?.quote;
      ambiguous = !error.response; // no response = network/timeout → outcome unknown
    } else if (error instanceof Error) {
      errorMessage = error.message;
      ambiguous = true;
    }

    console.error('Error creating PaymentIntent:', errorMessage);
    dispatch({ type: CREATE_PAYMENT_INTENT_FAILURE, error: errorMessage });
    return { status: 'error', ambiguous, message: errorMessage, code, quote };
  }
};

export default stripeReducer;
